import {useEffect, useMemo, useRef, useState} from "react";
import type {Path} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {string} from "yup";

import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {isEmpty} from "@pg-mono/nodash";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {useEmailVerificationApiError} from "@pg-rp/email-with-verification";

import {InquiryButtonTypeName} from "../../../../constants/inquiry_button_type";
import {TurnstileAction} from "../../../app/constants/turnstile_actions";
import {prefetchTurnstileToken} from "../../../app/turnstile";
import {useVerifyEmailQuery} from "../../../email_verification/api/email_verification_api";
import {validationMessages} from "../../../form/utils/validation_messages";
import {useAppSelector} from "../../../store/hooks";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {isValidationError} from "../../../utils/isValidationError";
import {applicationFormValidationSchema, initialEmptyApplicationValues} from "../../constants/form/application_form_fields";
import {getCheckboxConfigBySource} from "../../constants/lead/lead_modal_checkbox_options";
import {useAmplitudeLeadFormStartTracking} from "../../hooks/use_amplitude_lead_form_start_tracking";
import {useLeadData} from "../../hooks/use_lead_data";
import {LeadFormCloseSnapshotRef} from "../../hooks/use_lead_form_close_snapshot_ref";
import {useLeadFormErrorTracking} from "../../hooks/use_lead_form_error_tracking";
import {useLeadRequiredParams} from "../../hooks/use_lead_required_params";
import {getLeadFormCloseSnapshotFromFormValues} from "../../tracking/amplitude/get_amplitude_lead_form_inquiry_topics_selected";
import {IApplicationFormValues} from "../../types/form/IApplicationFormValues";
import {IAmplitudeLeadFormSubmitContext} from "../../types/lead/IAmplitudeLeadFormEventTracking";
import {isLeadFormSubmitAppError, isLeadFormSubmitErrorTracked} from "../../utils/is_lead_form_submit_app_error";
import {getRodoDataFromApplication} from "../../utils/lead/get_rodo_data_from_application";
import {normalizeLeadQuestions} from "../../utils/lead/normalize_lead_questions";
import {LeadFormQuestionsSlot} from "./slots/LeadFormQuestionsSlot";
import {LeadFormSubmitFooter} from "./LeadFormSubmitFooter";
import {LeadFormUserDataFields} from "./LeadFormUserDataFields";

type IPromotionLeadFormValues = IApplicationFormValues & {
    questions: string[];
    userMessage: string;
};

export const PROMOTION_LEAD_SUBMIT_CTA_TEXT = InquiryButtonTypeName.SUBMIT_APPLICATION;

interface IProps {
    defaultValues: IApplicationFormValues;
    formCloseSnapshotRef?: LeadFormCloseSnapshotRef;
    onSubmitDraft: (
        values: Pick<IApplicationFormValues, "name" | "email" | "phone" | "marketing_email" | "marketing_sms" | "message" | "additionalQuestions">,
        amplitudeSubmitContext?: IAmplitudeLeadFormSubmitContext
    ) => Promise<void>;
    smsLoginErrorMessage?: string | null;
    isFinalizing?: boolean;
}

const promotionFormValidationSchema = applicationFormValidationSchema.shape({
    userMessage: string().when("questions", {
        is: (questions: string[] = []) => questions.some((question) => question === "inny"),
        then: (schema) => schema.min(1, validationMessages.required).max(500, validationMessages.maxLength),
        otherwise: (schema) => schema.notRequired()
    })
});

export const LeadFormPromotion = (props: IProps) => {
    const {defaultValues, formCloseSnapshotRef, onSubmitDraft, smsLoginErrorMessage, isFinalizing = false} = props;
    const [email, setEmail] = useState<string | null>(null);
    const {data, error, isLoading, isFetching} = useVerifyEmailQuery({email: email || ""}, {skip: !email});
    const apiError = useEmailVerificationApiError({errorData: error});
    const {data: userProfile, isLoading: isUserProfileLoading} = useGetUserProfileQuery();
    const isAuthenticated = useIsAuthenticated();
    const source = useAppSelector((state) => state.mainLead.source);
    const amplitudeLeadFormEventTracking = useAppSelector((state) => state.mainLead.amplitudeLeadFormEventTracking);
    const didPrefetchTurnstileRef = useRef(false);

    const {vendorId, offerId, propertyId, sourceSection} = useLeadRequiredParams();
    const applicationData = useLeadData({
        vendorId,
        offerId: offerId || undefined,
        propertyId: propertyId || undefined
    });

    useAmplitudeLeadFormStartTracking({
        trackingContext: amplitudeLeadFormEventTracking,
        leadData: applicationData ?? undefined,
        source,
        sourceSection,
        initialLeadFormValues: defaultValues,
        isReady: !isUserProfileLoading
    });

    const {vendorName, offerPersonalDataProcessorName, rodoLinks} = getRodoDataFromApplication(applicationData || undefined);
    const checkboxConfig = getCheckboxConfigBySource(source);
    const allQuestions = [...checkboxConfig.baseOptions, ...checkboxConfig.moreOptions];

    const methods = useFormRHF<IPromotionLeadFormValues>({
        defaultValues: useMemo(
            () => ({
                ...initialEmptyApplicationValues,
                ...defaultValues,
                questions: [],
                userMessage: ""
            }),
            [defaultValues]
        ),
        resolver: yupResolver(promotionFormValidationSchema)
    });

    const {trackInvalidSubmit, trackApiError, markFieldErrorsReported} = useLeadFormErrorTracking({
        formErrors: methods.formState.errors,
        leadData: applicationData || undefined
    });

    useEffect(() => {
        methods.reset({
            ...initialEmptyApplicationValues,
            ...defaultValues,
            questions: [],
            userMessage: ""
        });
    }, [defaultValues, methods]);

    useEffect(() => {
        if (!apiError.isValid) {
            methods.setError("email", {message: apiError.message});
        }
    }, [apiError, methods]);

    useEffect(() => {
        if (!formCloseSnapshotRef) {
            return;
        }

        formCloseSnapshotRef.current = () => getLeadFormCloseSnapshotFromFormValues(methods.getValues());

        return () => {
            formCloseSnapshotRef.current = undefined;
        };
    }, [formCloseSnapshotRef, methods]);

    const ignoreVerification = !!methods.formState.defaultValues?.email && !email;
    const {root: rootError, ...fieldErrors} = methods.formState.errors;
    const disableSubmitButton = (!ignoreVerification && !data) || methods.formState.isSubmitting || !isEmpty(fieldErrors) || isLoading || isFetching;

    useEffect(() => {
        didPrefetchTurnstileRef.current = false;
    }, [isAuthenticated]);

    const preloadTurnstileToken = () => {
        if (didPrefetchTurnstileRef.current) {
            return;
        }

        didPrefetchTurnstileRef.current = true;
        prefetchTurnstileToken(isAuthenticated ? TurnstileAction.APPLICATION_CREATE : TurnstileAction.REGISTRATION);
    };

    if (!applicationData) {
        return <Loader size="lg" fullWidth />;
    }

    const handleSubmit = async (values: IPromotionLeadFormValues) => {
        methods.clearErrors("root" as Path<IPromotionLeadFormValues>);
        const {questions, userMessage, ...baseValues} = values;

        try {
            await onSubmitDraft(
                {
                    name: baseValues.name,
                    email: baseValues.email,
                    phone: baseValues.phone,
                    marketing_email: baseValues.marketing_email,
                    marketing_sms: baseValues.marketing_sms,
                    message: baseValues.message || defaultValues.message,
                    additionalQuestions: normalizeLeadQuestions(allQuestions, questions, userMessage)
                },
                {
                    ...(defaultValues.name || defaultValues.email || defaultValues.phone?.number ? {eventAction: "autofill" as const} : {})
                }
            );
        } catch (error) {
            // API failures of the lead send itself are already reported by `sendLead` - report here only
            // the ones raised before it (registration, profile sync) by the draft submit callback.
            if (isLeadFormSubmitAppError(error) && !isLeadFormSubmitErrorTracked(error)) {
                trackApiError(error.appError);
            }

            if (isValidationError(error)) {
                const {fieldErrors, nonFieldErrors} = error.appError;

                // Already reported as an API error - don't report the same fields again once `setError` runs.
                markFieldErrorsReported(Object.fromEntries(Object.entries(fieldErrors).map(([fieldName, messages]) => [fieldName, messages.join(", ")])));

                for (const fieldName of Object.keys(fieldErrors)) {
                    const errorMessage = fieldErrors[fieldName];
                    methods.setError(fieldName as Path<IPromotionLeadFormValues>, {
                        message: errorMessage.join(", ")
                    });
                }

                if (nonFieldErrors.length) {
                    methods.setError("root" as Path<IPromotionLeadFormValues>, {
                        message: nonFieldErrors.join(", ")
                    });
                }

                return;
            }

            methods.setError("root" as Path<IPromotionLeadFormValues>, {
                message: "Nie udało się wysłać zgłoszenia."
            });
        }
    };

    return (
        <FormProviderRHF {...methods}>
            <form
                className={formWrapperStyle}
                onSubmit={methods.handleSubmit(handleSubmit, (errors) => trackInvalidSubmit(errors, amplitudeLeadFormEventTracking?.cta_text ?? null))}
                onFocusCapture={preloadTurnstileToken}
                onClick={preloadTurnstileToken}
            >
                <LeadFormUserDataFields
                    verificationData={data}
                    verificationTrigger={setEmail}
                    disablePhone={Boolean(userProfile?.phone && userProfile.phone_verified)}
                    smsLoginErrorMessage={smsLoginErrorMessage}
                />

                <LeadFormQuestionsSlot isTwoColumnGrid />

                <div className={footerWrapperStyle}>
                    {rootError?.message && (
                        <Text as="p" variant="info_txt_1" className={formErrorStyle}>
                            {rootError.message}
                        </Text>
                    )}

                    <LeadFormSubmitFooter
                        sendDisabled={disableSubmitButton || isFinalizing}
                        isSending={methods.formState.isSubmitting || isFinalizing}
                        buttonText={PROMOTION_LEAD_SUBMIT_CTA_TEXT}
                        vendorName={vendorName}
                        offerPersonalDataProcessorName={offerPersonalDataProcessorName}
                        rodoLinks={rodoLinks}
                    />
                </div>
            </form>
        </FormProviderRHF>
    );
};

const formWrapperStyle = css({
    h: "100%",
    md: {
        h: "auto"
    }
});

const footerWrapperStyle = css({
    position: "sticky",
    bottom: 0,
    zIndex: 20,
    pt: 2,
    backgroundColor: "var(--colors-white)",
    _before: {
        content: '""',
        position: "absolute",
        top: "-24px",
        left: 0,
        right: 0,
        h: "24px",
        background: "linear-gradient(to top, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))",
        pointerEvents: "none"
    },
    md: {
        position: "static",
        pt: 0,
        backgroundColor: "transparent",
        _before: {
            display: "none"
        }
    }
});

const formErrorStyle = css({
    color: "var(--colors-danger)",
    mb: 2,
    textAlign: "center"
});
import {useEffect, useMemo, useRef, useState} from "react";
import type {Path} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import {string} from "yup";

import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {isEmpty} from "@pg-mono/nodash";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {useEmailVerificationApiError} from "@pg-rp/email-with-verification";

import {InquiryButtonTypeName} from "../../../../constants/inquiry_button_type";
import {TurnstileAction} from "../../../app/constants/turnstile_actions";
import {prefetchTurnstileToken} from "../../../app/turnstile";
import {useVerifyEmailQuery} from "../../../email_verification/api/email_verification_api";
import {validationMessages} from "../../../form/utils/validation_messages";
import {useAppSelector} from "../../../store/hooks";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {isValidationError} from "../../../utils/isValidationError";
import {applicationFormValidationSchema, initialEmptyApplicationValues} from "../../constants/form/application_form_fields";
import {getCheckboxConfigBySource} from "../../constants/lead/lead_modal_checkbox_options";
import {useAmplitudeLeadFormStartTracking} from "../../hooks/use_amplitude_lead_form_start_tracking";
import {useLeadData} from "../../hooks/use_lead_data";
import {LeadFormCloseSnapshotRef} from "../../hooks/use_lead_form_close_snapshot_ref";
import {useLeadFormErrorTracking} from "../../hooks/use_lead_form_error_tracking";
import {useLeadRequiredParams} from "../../hooks/use_lead_required_params";
import {getLeadFormCloseSnapshotFromFormValues} from "../../tracking/amplitude/get_amplitude_lead_form_inquiry_topics_selected";
import {IApplicationFormValues} from "../../types/form/IApplicationFormValues";
import {IAmplitudeLeadFormSubmitContext} from "../../types/lead/IAmplitudeLeadFormEventTracking";
import {isLeadFormSubmitAppError, isLeadFormSubmitErrorTracked} from "../../utils/is_lead_form_submit_app_error";
import {getRodoDataFromApplication} from "../../utils/lead/get_rodo_data_from_application";
import {normalizeLeadQuestions} from "../../utils/lead/normalize_lead_questions";
import {LeadFormQuestionsSlot} from "./slots/LeadFormQuestionsSlot";
import {LeadFormSubmitFooter} from "./LeadFormSubmitFooter";
import {LeadFormUserDataFields} from "./LeadFormUserDataFields";

type IPromotionLeadFormValues = IApplicationFormValues & {
    questions: string[];
    userMessage: string;
};

export const PROMOTION_LEAD_SUBMIT_CTA_TEXT = InquiryButtonTypeName.SUBMIT_APPLICATION;

interface IProps {
    defaultValues: IApplicationFormValues;
    formCloseSnapshotRef?: LeadFormCloseSnapshotRef;
    onSubmitDraft: (
        values: Pick<IApplicationFormValues, "name" | "email" | "phone" | "marketing_email" | "marketing_sms" | "message" | "additionalQuestions">,
        amplitudeSubmitContext?: IAmplitudeLeadFormSubmitContext
    ) => Promise<void>;
    smsLoginErrorMessage?: string | null;
    isFinalizing?: boolean;
}

const promotionFormValidationSchema = applicationFormValidationSchema.shape({
    userMessage: string().when("questions", {
        is: (questions: string[] = []) => questions.some((question) => question === "inny"),
        then: (schema) => schema.min(1, validationMessages.required).max(500, validationMessages.maxLength),
        otherwise: (schema) => schema.notRequired()
    })
});

export const LeadFormPromotion = (props: IProps) => {
    const {defaultValues, formCloseSnapshotRef, onSubmitDraft, smsLoginErrorMessage, isFinalizing = false} = props;
    const [email, setEmail] = useState<string | null>(null);
    const {data, error, isLoading, isFetching} = useVerifyEmailQuery({email: email || ""}, {skip: !email});
    const apiError = useEmailVerificationApiError({errorData: error});
    const {data: userProfile, isLoading: isUserProfileLoading} = useGetUserProfileQuery();
    const isAuthenticated = useIsAuthenticated();
    const source = useAppSelector((state) => state.mainLead.source);
    const amplitudeLeadFormEventTracking = useAppSelector((state) => state.mainLead.amplitudeLeadFormEventTracking);
    const didPrefetchTurnstileRef = useRef(false);

    const {vendorId, offerId, propertyId, sourceSection} = useLeadRequiredParams();
    const applicationData = useLeadData({
        vendorId,
        offerId: offerId || undefined,
        propertyId: propertyId || undefined
    });

    useAmplitudeLeadFormStartTracking({
        trackingContext: amplitudeLeadFormEventTracking,
        leadData: applicationData ?? undefined,
        source,
        sourceSection,
        initialLeadFormValues: defaultValues,
        isReady: !isUserProfileLoading
    });

    const {vendorName, offerPersonalDataProcessorName, rodoLinks} = getRodoDataFromApplication(applicationData || undefined);
    const checkboxConfig = getCheckboxConfigBySource(source);
    const allQuestions = [...checkboxConfig.baseOptions, ...checkboxConfig.moreOptions];

    const methods = useFormRHF<IPromotionLeadFormValues>({
        defaultValues: useMemo(
            () => ({
                ...initialEmptyApplicationValues,
                ...defaultValues,
                questions: [],
                userMessage: ""
            }),
            [defaultValues]
        ),
        resolver: yupResolver(promotionFormValidationSchema)
    });

    const {trackInvalidSubmit, trackApiError, markFieldErrorsReported} = useLeadFormErrorTracking({
        formErrors: methods.formState.errors,
        leadData: applicationData || undefined
    });

    useEffect(() => {
        methods.reset({
            ...initialEmptyApplicationValues,
            ...defaultValues,
            questions: [],
            userMessage: ""
        });
    }, [defaultValues, methods]);

    useEffect(() => {
        if (!apiError.isValid) {
            methods.setError("email", {message: apiError.message});
        }
    }, [apiError, methods]);

    useEffect(() => {
        if (!formCloseSnapshotRef) {
            return;
        }

        formCloseSnapshotRef.current = () => getLeadFormCloseSnapshotFromFormValues(methods.getValues());

        return () => {
            formCloseSnapshotRef.current = undefined;
        };
    }, [formCloseSnapshotRef, methods]);

    const ignoreVerification = !!methods.formState.defaultValues?.email && !email;
    const {root: rootError, ...fieldErrors} = methods.formState.errors;
    const disableSubmitButton = (!ignoreVerification && !data) || methods.formState.isSubmitting || !isEmpty(fieldErrors) || isLoading || isFetching;

    useEffect(() => {
        didPrefetchTurnstileRef.current = false;
    }, [isAuthenticated]);

    const preloadTurnstileToken = () => {
        if (didPrefetchTurnstileRef.current) {
            return;
        }

        didPrefetchTurnstileRef.current = true;
        prefetchTurnstileToken(isAuthenticated ? TurnstileAction.APPLICATION_CREATE : TurnstileAction.REGISTRATION);
    };

    if (!applicationData) {
        return <Loader size="lg" fullWidth />;
    }

    const handleSubmit = async (values: IPromotionLeadFormValues) => {
        methods.clearErrors("root" as Path<IPromotionLeadFormValues>);
        const {questions, userMessage, ...baseValues} = values;

        try {
            await onSubmitDraft(
                {
                    name: baseValues.name,
                    email: baseValues.email,
                    phone: baseValues.phone,
                    marketing_email: baseValues.marketing_email,
                    marketing_sms: baseValues.marketing_sms,
                    message: baseValues.message || defaultValues.message,
                    additionalQuestions: normalizeLeadQuestions(allQuestions, questions, userMessage)
                },
                {
                    ...(defaultValues.name || defaultValues.email || defaultValues.phone?.number ? {eventAction: "autofill" as const} : {})
                }
            );
        } catch (error) {
            // API failures of the lead send itself are already reported by `sendLead` - report here only
            // the ones raised before it (registration, profile sync) by the draft submit callback.
            if (isLeadFormSubmitAppError(error) && !isLeadFormSubmitErrorTracked(error)) {
                trackApiError(error.appError);
            }

            if (isValidationError(error)) {
                const {fieldErrors, nonFieldErrors} = error.appError;

                // Already reported as an API error - don't report the same fields again once `setError` runs.
                markFieldErrorsReported(Object.fromEntries(Object.entries(fieldErrors).map(([fieldName, messages]) => [fieldName, messages.join(", ")])));

                for (const fieldName of Object.keys(fieldErrors)) {
                    const errorMessage = fieldErrors[fieldName];
                    methods.setError(fieldName as Path<IPromotionLeadFormValues>, {
                        message: errorMessage.join(", ")
                    });
                }

                if (nonFieldErrors.length) {
                    methods.setError("root" as Path<IPromotionLeadFormValues>, {
                        message: nonFieldErrors.join(", ")
                    });
                }

                return;
            }

            methods.setError("root" as Path<IPromotionLeadFormValues>, {
                message: "Nie udało się wysłać zgłoszenia."
            });
        }
    };

    return (
        <FormProviderRHF {...methods}>
            <form
                className={formWrapperStyle}
                onSubmit={methods.handleSubmit(handleSubmit, (errors) => trackInvalidSubmit(errors, amplitudeLeadFormEventTracking?.cta_text ?? null))}
                onFocusCapture={preloadTurnstileToken}
                onClick={preloadTurnstileToken}
            >
                <LeadFormUserDataFields
                    verificationData={data}
                    verificationTrigger={setEmail}
                    disablePhone={Boolean(userProfile?.phone && userProfile.phone_verified)}
                    smsLoginErrorMessage={smsLoginErrorMessage}
                />

                <LeadFormQuestionsSlot isTwoColumnGrid />

                <div className={footerWrapperStyle}>
                    {rootError?.message && (
                        <Text as="p" variant="info_txt_1" className={formErrorStyle}>
                            {rootError.message}
                        </Text>
                    )}

                    <LeadFormSubmitFooter
                        sendDisabled={disableSubmitButton || isFinalizing}
                        isSending={methods.formState.isSubmitting || isFinalizing}
                        buttonText={PROMOTION_LEAD_SUBMIT_CTA_TEXT}
                        vendorName={vendorName}
                        offerPersonalDataProcessorName={offerPersonalDataProcessorName}
                        rodoLinks={rodoLinks}
                    />
                </div>
            </form>
        </FormProviderRHF>
    );
};

const formWrapperStyle = css({
    h: "100%",
    md: {
        h: "auto"
    }
});

const footerWrapperStyle = css({
    position: "sticky",
    bottom: 0,
    zIndex: 20,
    pt: 2,
    backgroundColor: "var(--colors-white)",
    _before: {
        content: '""',
        position: "absolute",
        top: "-24px",
        left: 0,
        right: 0,
        h: "24px",
        background: "linear-gradient(to top, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))",
        pointerEvents: "none"
    },
    md: {
        position: "static",
        pt: 0,
        backgroundColor: "transparent",
        _before: {
            display: "none"
        }
    }
});

const formErrorStyle = css({
    color: "var(--colors-danger)",
    mb: 2,
    textAlign: "center"
});
